import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const TOP_HEIGHT = 280;
const BOTTOM_HEIGHT = 170;
const TOTAL_HEIGHT = TOP_HEIGHT + BOTTOM_HEIGHT;
const WIDTH = 700;
const TOP_MARGIN = { top: 20, right: 20, bottom: 30, left: 60 };
const BOTTOM_MARGIN = { top: 20, right: 20, bottom: 40, left: 60 };

const DECILE_KEYS = ['d10', 'd20', 'd30', 'd40', 'd50', 'd60', 'd70', 'd80', 'd90'];
const BAND_LABELS = [
  '10th-20th',
  '20th-30th',
  '30th-40th',
  '40th-50th',
  '50th-60th',
  '60th-70th',
  '70th-80th',
  '80th-90th',
];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function PriceDecileChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  // Sequential blue-to-red color scale for 8 bands (between 9 decile boundaries)
  const bandColors = useMemo(() => {
    return d3.scaleSequential(d3.interpolateRdYlBu)
      // Reverse so low deciles are blue, high deciles are red
      .domain([BAND_LABELS.length - 1, 0]);
  }, []);

  // Same color scale for the 10 bars in the bottom panel (D1-D10)
  const barColors = useMemo(() => {
    return d3.scaleSequential(d3.interpolateRdYlBu)
      .domain([9, 0]);
  }, []);

  useEffect(() => {
    if (!data || !data.deciles || data.deciles.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg
      .attr('width', '100%')
      .attr('height', TOTAL_HEIGHT)
      .attr('viewBox', `0 0 ${WIDTH} ${TOTAL_HEIGHT}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    // ===================== TOP PANEL: Stacked Area (Decile Bands) =====================
    const topG = svg.append('g').attr('transform', `translate(${TOP_MARGIN.left}, ${TOP_MARGIN.top})`);
    const topInnerW = WIDTH - TOP_MARGIN.left - TOP_MARGIN.right;
    const topInnerH = TOP_HEIGHT - TOP_MARGIN.top - TOP_MARGIN.bottom;

    const deciles = data.deciles;
    const dates = deciles.map((d) => d.date);

    // Collect all decile values for Y domain
    const allVals = [];
    deciles.forEach((d) => {
      DECILE_KEYS.forEach((k) => {
        if (d[k] != null) allVals.push(d[k]);
      });
    });
    const yMin = d3.min(allVals);
    const yMax = d3.max(allVals);
    const yPad = (yMax - yMin) * 0.05 || 1;

    const xTop = d3.scalePoint().domain(dates).range([0, topInnerW]);
    const yTop = d3.scaleLinear().domain([yMin - yPad, yMax + yPad]).nice().range([topInnerH, 0]);

    // Grid lines
    const yTicks = yTop.ticks(6);
    topG.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', topInnerW)
      .attr('y1', (d) => yTop(d))
      .attr('y2', (d) => yTop(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Draw bands between consecutive decile boundaries
    const areaGen = d3.area()
      .x((d) => xTop(d.date))
      .y0((d) => yTop(d.lower))
      .y1((d) => yTop(d.upper))
      .curve(d3.curveMonotoneX);

    for (let i = 0; i < DECILE_KEYS.length - 1; i++) {
      const lowerKey = DECILE_KEYS[i];
      const upperKey = DECILE_KEYS[i + 1];
      const bandData = deciles.map((d) => ({
        date: d.date,
        lower: d[lowerKey],
        upper: d[upperKey],
      }));

      topG.append('path')
        .datum(bandData)
        .attr('d', areaGen)
        .attr('fill', bandColors(i))
        .attr('fill-opacity', 0.7)
        .attr('stroke', '#fff')
        .attr('stroke-width', 0.5);
    }

    // Median line (d50)
    const medianLine = d3.line()
      .x((d) => xTop(d.date))
      .y((d) => yTop(d.d50))
      .curve(d3.curveMonotoneX);

    topG.append('path')
      .datum(deciles)
      .attr('d', medianLine)
      .attr('fill', 'none')
      .attr('stroke', '#1f2937')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,3');

    // X axis
    const tickCount = Math.min(dates.length, 12);
    const tickInterval = Math.max(1, Math.floor(dates.length / tickCount));
    const tickValues = dates.filter((_, i) => i % tickInterval === 0);

    topG.append('g')
      .attr('transform', `translate(0, ${topInnerH})`)
      .call(
        d3.axisBottom(xTop)
          .tickValues(tickValues)
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Y axis
    topG.append('g')
      .call(
        d3.axisLeft(yTop)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => `$${v}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    topG.selectAll('.domain').attr('stroke', '#e5e7eb');
    topG.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Crosshair + hover overlay for top panel
    const crosshair = topG.append('line')
      .attr('y1', 0)
      .attr('y2', topInnerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const colWidth = dates.length > 1 ? topInnerW / (dates.length - 1) : topInnerW;

    dates.forEach((date, i) => {
      const cx = xTop(date);
      const halfW = colWidth / 2;
      const row = deciles[i];

      topG.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', topInnerH)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event) => {
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          setTooltip({
            type: 'area',
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            decileValues: DECILE_KEYS.map((k) => ({
              label: k.toUpperCase(),
              value: row[k],
            })),
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    // ===================== BOTTOM PANEL: Bar Chart (Current Distribution) =====================
    const currentDist = data.currentDistribution;
    if (!currentDist || currentDist.length === 0) return;

    const bottomYOffset = TOP_HEIGHT;
    const bottomG = svg.append('g').attr('transform', `translate(${BOTTOM_MARGIN.left}, ${bottomYOffset + BOTTOM_MARGIN.top})`);
    const bottomInnerW = WIDTH - BOTTOM_MARGIN.left - BOTTOM_MARGIN.right;
    const bottomInnerH = BOTTOM_HEIGHT - BOTTOM_MARGIN.top - BOTTOM_MARGIN.bottom;

    // Separator line between panels
    svg.append('line')
      .attr('x1', TOP_MARGIN.left)
      .attr('x2', WIDTH - TOP_MARGIN.right)
      .attr('y1', bottomYOffset)
      .attr('y2', bottomYOffset)
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1);

    // Section label
    svg.append('text')
      .attr('x', TOP_MARGIN.left)
      .attr('y', bottomYOffset + 14)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#6b7280')
      .text('Current Distribution');

    const decileLabels = currentDist.map((d) => d.decile);
    const maxCount = d3.max(currentDist, (d) => d.count);

    const xBottom = d3.scaleBand()
      .domain(decileLabels)
      .range([0, bottomInnerW])
      .padding(0.25);

    const yBottom = d3.scaleLinear()
      .domain([0, maxCount * 1.15])
      .nice()
      .range([bottomInnerH, 0]);

    // Grid lines
    const yBottomTicks = yBottom.ticks(4);
    bottomG.selectAll('.grid-line')
      .data(yBottomTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', bottomInnerW)
      .attr('y1', (d) => yBottom(d))
      .attr('y2', (d) => yBottom(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Bars
    bottomG.selectAll('.bar')
      .data(currentDist)
      .enter()
      .append('rect')
      .attr('x', (d) => xBottom(d.decile))
      .attr('y', (d) => yBottom(d.count))
      .attr('width', xBottom.bandwidth())
      .attr('height', (d) => bottomInnerH - yBottom(d.count))
      .attr('fill', (_, i) => barColors(i))
      .attr('fill-opacity', 0.85)
      .attr('rx', 2)
      .style('cursor', 'pointer')
      .on('mousemove', (event, d) => {
        setTooltip({
          type: 'bar',
          x: event.clientX + 12,
          y: event.clientY - 10,
          decile: d.decile,
          count: d.count,
          minPrice: d.minPrice,
          maxPrice: d.maxPrice,
          avgPrice: d.avgPrice,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    // Value labels on bars
    bottomG.selectAll('.bar-label')
      .data(currentDist)
      .enter()
      .append('text')
      .attr('x', (d) => xBottom(d.decile) + xBottom.bandwidth() / 2)
      .attr('y', (d) => yBottom(d.count) - 4)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text((d) => d.count);

    // X axis
    bottomG.append('g')
      .attr('transform', `translate(0, ${bottomInnerH})`)
      .call(d3.axisBottom(xBottom).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis
    bottomG.append('g')
      .call(
        d3.axisLeft(yBottom)
          .ticks(4)
          .tickSize(0)
          .tickFormat((v) => v)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    bottomG.selectAll('.domain').attr('stroke', '#e5e7eb');
    bottomG.selectAll('.tick line').attr('stroke', '#e5e7eb');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, bandColors, barColors]);

  // Empty state
  if (!data || !data.deciles || data.deciles.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Decile Analysis</div>
          <div style={styles.subtitle}>Distribution of prices across deciles over time and current snapshot</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Decile Analysis</div>
        <div style={styles.subtitle}>Distribution of prices across deciles over time and current snapshot</div>
      </div>

      {/* Legend for decile bands */}
      <div style={styles.legend}>
        {BAND_LABELS.map((label, i) => (
          <div key={label} style={styles.legendItem}>
            <svg width="14" height="14">
              <rect x="0" y="0" width="14" height="14" fill={bandColors(i)} fillOpacity="0.7" rx="2" />
            </svg>
            <span>{label}</span>
          </div>
        ))}
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#1f2937" strokeWidth="1.5" strokeDasharray="4,3" />
          </svg>
          <span>Median (D50)</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && tooltip.type === 'area' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltip.decileValues.map((dv) => (
            <div
              key={dv.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: '#6b7280' }}>{dv.label}</span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>
                ${dv.value != null ? dv.value.toFixed(2) : '--'}
              </span>
            </div>
          ))}
        </div>
      )}

      {tooltip && tooltip.type === 'bar' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.decile}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Count</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>{tooltip.count}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Min Price</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              ${tooltip.minPrice != null ? tooltip.minPrice.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Max Price</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              ${tooltip.maxPrice != null ? tooltip.maxPrice.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Avg Price</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              ${tooltip.avgPrice != null ? tooltip.avgPrice.toFixed(2) : '--'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
